import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaPencilAlt } from 'react-icons/fa';
import { MdDelete } from 'react-icons/md';

import { Text, toasts, ModalMethods } from '../../../../ui-component';
import { api } from '../../../../utils/apiRequest';
import VariantGroupsModal from './VariantGroupsModal';
import ModifyVariantModal from './ModifyVariantModal';
import ModifyTaxModal from './ModifyTaxModal';
import ProductDescriptionModal from './ProductDescriptionModal';
import WorkFlowModal from './WorkFlowModal';
import BulkApplyModal from './BulkApplyModal';
import IconSelectorModal, { IconType } from '../AddItem/IconSelectorModal';
import {
  getValueFromLocalStorage,
  convertNumberOrStringToPriceFormat,
} from '../../../../utils/common';

import piece from '../../../../assets/icons/Stack.png';
import wear from '../../../../assets/icons/Tag.png';
import placeholderPieceIcon from '../../../../assets/images/image 354.png';

type TaxBreakdown = {
  name: string;
  value: number;
  is_default?: boolean | null;
};

type PricingRule = {
  id: number;
  operator: string;
  price: string;
  gst: number;
};

type Tax = {
  id: number;
  tax_type: 'FLAT' | 'ADVANCE';
  tax_name: string;
  flat_gst: number | null;
  pricing_rules: PricingRule[];
  is_default?: boolean;
};

type ProductPiece = {
  name: string;
  image_ref_id?: string;
  image_url?: string;
};

type ProductTag = {
  id: number;
  name: string;
  boutique_id: number;
};

type ProductUnit = {
  id: number;
  name: string;
  boutique_id: number;
};

type ProductItem = {
  id: number;
  boutique_id: number;
  name: string;
  display_name?: string;
  gender?: string;
  category_id?: number;
  category?: string;
  tags?: ProductTag[];
  hsn_code?: string;
  product_description?: string;
  tax_included?: boolean;
  tax_data?: { name: string; value: number; is_default?: boolean | null };
  tax_breakdown?: TaxBreakdown[];
  tax_type?: string;
  base_selling_price?: number;
  min_value?: number;
  max_value?: number;
  cost_price?: number;
  unit?: ProductUnit | null;
  cover_image_url?: string | null;
  cover_image_ref_id?: string | null;
  image_urls?: string[];
  pieces?: ProductPiece[];
  pieces_count?: number;
  taxes?: Tax[];
  order_type_id?: number;
  is_valid?: boolean;
};

type VariantGroup = {
  id: number;
  name: string;
  values: string[];
};

type Variant = {
  id: number;
  name: string;
  readyMadeOutfitId: number;
  variantAttributes: Record<string, string>;
  sku: string;
  barcode: string;
  defaultCodeType: 'QR' | 'BARCODE' | null;
  existingCodeType: 'QR_CODE' | 'BARCODE' | null;
  existingCodeValue: string | null;
  price: number;
  sellingPrice: number;
  costPrice: number;
  stock: number;
  unit: string;
  lowStockAlert: number;
  storageBinId: number | null;
  storageBinName: string | null;
  storageLocation: string | null;
  imageRefIds: string[] | null;
  imageUrls: string[] | null;
};

type ProductionWorker = {
  id: number;
  user_id: number;
  user_name: string;
  user_phone_number?: string;
  administrative_role_name?: string | null;
  skilled_role_names?: string[] | null;
  status?: string;
  employment_type?: string;
  user_profile_picture_url?: string | null;
};

type ProductionStep = {
  process_name: string;
  days_to_be_take: number;
  worker_cost_to_be_take: number;
  material_cost_to_be_take: number;
  workers: ProductionWorker[];
  sort_index: number;
};

type ProductionProcess = {
  boutique_id: number;
  atomic_outfit_id: number;
  readymade_outfit_id: number;
  steps: ProductionStep[];
};

type FullDetailsResponse = {
  item: ProductItem;
  variant_groups: VariantGroup[];
  variant_group_suggestions?: VariantGroup[];
  variants: Variant[];
  production_process: ProductionProcess | null;
  label_config: unknown;
};

const COLOR_HEX_MAP: Record<string, string> = {
  white: '#FFFFFF',
  black: '#000000',
  red: '#EF4444',
  blue: '#3B82F6',
  green: '#22C55E',
  yellow: '#EAB308',
  orange: '#F97316',
  pink: '#EC4899',
  purple: '#A855F7',
  brown: '#92400E',
  grey: '#9CA3AF',
  gray: '#9CA3AF',
  navy: '#1E3A5F',
  maroon: '#800000',
  beige: '#F5F5DC',
  gold: '#D4A017',
  silver: '#C0C0C0',
};

const getColorHex = (colorName: string): string => {
  return COLOR_HEX_MAP[colorName.toLowerCase()] || '#9CA3AF';
};

const isColorGroup = (group: VariantGroup): boolean => {
  const name = group.name.toLowerCase();

  return name === 'color' || name === 'colour' || name === 'colors' || name === 'colours';
};

const calcProfitMargin = (sellingPrice: number, costPrice: number): number => {
  if (sellingPrice <= 0) {
    return 0;
  }

  return Math.round(((sellingPrice - costPrice) / sellingPrice) * 100);
};

const EditProduct = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const variantGroupsModalRef = useRef<ModalMethods>(null);
  const modifyVariantModalRef = useRef<ModalMethods>(null);
  const modifyTaxModalRef = useRef<ModalMethods>(null);
  const productDescModalRef = useRef<ModalMethods>(null);
  const pieceIconSelectorRef = useRef<ModalMethods>(null);
  const workFlowModalRef = useRef<ModalMethods>(null);
  const bulkApplyModalRef = useRef<ModalMethods>(null);

  const [item, setItem] = useState<ProductItem | null>(null);
  const [variantGroups, setVariantGroups] = useState<VariantGroup[]>([]);
  const [variantGroupSuggestions, setVariantGroupSuggestions] = useState<VariantGroup[]>([]);
  const [variants, setVariants] = useState<Variant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [previewColor, setPreviewColor] = useState<string>('');
  const [previewSize, setPreviewSize] = useState<string>('');
  const [selectedVariant, setSelectedVariant] = useState<Variant | null>(null);
  const [newPieceName, setNewPieceName] = useState('');
  const [editingPieceIdx, setEditingPieceIdx] = useState<number | null>(null);
  const [editingPieceName, setEditingPieceName] = useState('');
  const [isSavingPieces, setIsSavingPieces] = useState(false);
  const [iconSelectTarget, setIconSelectTarget] = useState<'new' | number | null>(null);
  const [productionProcess, setProductionProcess] = useState<ProductionProcess | null>(null);

  const fetchProductDetails = useCallback(async () => {
    try {
      setIsLoading(true);
      const boutiqueId = getValueFromLocalStorage('boutique_id');

      if (!boutiqueId) {
        toasts('error', 'Boutique ID not found', 'boutique-id-error');
        navigate('/settings/items-and-categories');

        return;
      }

      const response = await api.getRequest(`readymade/${id}/full-details`);

      if (response.status && response.data) {
        const data = response.data as FullDetailsResponse;

        setItem(data.item);
        setVariantGroups(data.variant_groups || []);
        setVariantGroupSuggestions(data.variant_group_suggestions || []);
        setVariants(data.variants || []);
        setProductionProcess(data.production_process || null);

        const colorGrp = (data.variant_groups || []).find((g) => isColorGroup(g));
        const sizeGrp = (data.variant_groups || []).find((g) => g.name.toLowerCase() === 'size');

        if (colorGrp?.values?.length) {
          setPreviewColor(colorGrp.values[0]);
        }

        if (sizeGrp?.values?.length) {
          setPreviewSize(sizeGrp.values[sizeGrp.values.length - 1]);
        }
      } else {
        toasts('error', response.message || 'Failed to load product', 'product-load-error');
        navigate('/settings/items-and-categories');
      }
    } catch (err) {
      if (err instanceof Error) {
        toasts('error', err.message, 'product-load-error');
        navigate('/settings/items-and-categories');
      }
    } finally {
      setIsLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (id) {
      fetchProductDetails();
    }
  }, [id, fetchProductDetails]);

  const getProfitMarginStyle = (margin: number): string => {
    if (margin > 0) {
      return 'text-green-600';
    }

    if (margin < 0) {
      return 'text-red-500';
    }

    return 'text-orange-500';
  };

  const renderHeader = () => {
    return (
      <div className="flex items-center justify-between mb-4 px-6 py-4 bg-white">
        <p className="font-inter font-semibold text-sm leading-2 tracking-[0.07px] text-gray-600 mt-1 max-w-2xl">
          Set pieces (optional), images, base prices, variants, barcodes, inventory and online
          visibility.
        </p>
      </div>
    );
  };

  const renderVariantGroups = () => {
    return (
      <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-lg">⊞</span>
            <p className="font-inter font-bold text-[1.1rem]">Variant Groups</p>
          </div>
          <button
            type="button"
            onClick={() => variantGroupsModalRef.current?.show()}
            className="px-4 py-2 border border-blue-500 text-blue-600 rounded-2xl text-sm font-medium flex items-center gap-2 hover:bg-blue-50 transition-colors"
          >
            <FaPencilAlt size={12} />
            <span>Modify</span>
          </button>
        </div>
        <p className="font-inter text-sm text-gray-500 mb-5">
          Define how variants are generated. For example Size: S, M, L and Color: Black, Red.
          Combinations will become rows in the variant matrix.
        </p>

        <div className="space-y-3">
          {variantGroups.map((group) => {
            const isColor = isColorGroup(group);

            return (
              <div
                key={group.id}
                className="border border-gray-200 rounded-xl p-4 flex items-center justify-between"
              >
                <div className="flex-1">
                  <p className="font-inter font-semibold text-[15px] mb-2.5">{group.name}</p>
                  <div className="flex flex-wrap items-center gap-3">
                    {group.values.map((val) =>
                      isColor ? (
                        <div key={val} className="flex items-center gap-1.5">
                          <span
                            className="w-5 h-5 rounded-full border border-gray-300 inline-block"
                            style={{ backgroundColor: getColorHex(val) }}
                          />
                          <span className="text-sm text-gray-700">{val}</span>
                        </div>
                      ) : (
                        <span
                          key={val}
                          className="px-3 py-1 bg-gray-100 rounded-md text-sm text-gray-700 font-medium"
                        >
                          {val}
                        </span>
                      )
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  className="flex items-center gap-1 text-sm text-gray-400 hover:text-gray-600 ml-4 flex-shrink-0 border border-gray-200 rounded-full px-3 py-1"
                >
                  <span>{group.values.length} Options</span>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 16 16"
                    fill="none"
                    className="text-gray-400"
                  >
                    <path
                      d="M6 4l4 4-4 4"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            );
          })}

          {variantGroups.length === 0 && !isLoading && (
            <div className="text-center py-6">
              <p className="text-sm text-gray-500">
                No variant groups configured. Click Modify to add groups.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderVariantMatrix = () => {
    return (
      <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-lg">◈</span>
          <p className="font-inter font-bold text-[1.1rem]">Variant Matrix</p>
        </div>
        <p className="font-inter text-sm text-gray-500 mb-4">
          Each row represents a sellable variant. Set SKU, barcode, price override (optional),
          inventory quantity and low-stock alert.
        </p>

        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={() => bulkApplyModalRef.current?.show()}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 flex items-center gap-2 hover:bg-gray-50 transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M2 4h12M2 8h12M2 12h12"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            Bulk Apply
          </button>

          <div className="flex items-center gap-1 text-xs text-gray-400 font-medium">
            <span>Location</span>
            <span>·</span>
            <span>Price</span>
            <span>·</span>
            <span>Stock</span>
            <span>·</span>
            <span>Low</span>
          </div>
        </div>

        <div className="space-y-3 max-h-[600px] overflow-y-auto pr-1">
          {variants.map((variant) => {
            const profitMargin = calcProfitMargin(variant.sellingPrice, variant.costPrice);

            return (
              <div key={variant.id} className="border border-gray-200 rounded-xl p-4 bg-[#FAFBFD]">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center overflow-hidden flex-shrink-0">
                      {item?.cover_image_url ? (
                        <img
                          src={item.cover_image_url}
                          alt={variant.name}
                          className="w-full h-full object-cover rounded-lg"
                        />
                      ) : (
                        <span className="text-xl">👕</span>
                      )}
                    </div>
                    <div>
                      <p className="font-inter font-bold text-[15px] leading-tight">
                        {variant.name}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        <span className="font-semibold">SKU:</span> {variant.sku}
                      </p>
                      <p className="text-xs text-gray-500">
                        <span className="font-semibold">Bar Code:</span> {variant.barcode}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedVariant(variant);
                      modifyVariantModalRef.current?.show();
                    }}
                    className="px-4 py-1.5 border border-blue-500 text-blue-600 rounded-2xl text-xs font-medium flex items-center gap-1.5 hover:bg-blue-50 transition-colors"
                  >
                    <FaPencilAlt size={10} />
                    <span>Modify</span>
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                  <div>
                    <span className="font-semibold text-gray-800">Selling Price: </span>
                    <span className="text-gray-600">
                      ₹ {convertNumberOrStringToPriceFormat(variant.sellingPrice)}
                    </span>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-800">Stock: </span>
                    <span className="text-gray-600">
                      {variant.stock} {variant.unit?.toLowerCase() || 'pieces'}
                    </span>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-800">Cost Price: </span>
                    <span className="text-gray-600">
                      ₹ {convertNumberOrStringToPriceFormat(variant.costPrice)}
                    </span>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-800">Low Stock reminder: </span>
                    <span className="text-gray-600">
                      {variant.lowStockAlert} {variant.unit?.toLowerCase() || 'pieces'}
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
                  <div className="text-sm">
                    <span className="font-semibold text-gray-800">Storage Location: </span>
                    <span className="text-gray-600">
                      {[variant.storageLocation, variant.storageBinName]
                        .filter(Boolean)
                        .join(' • ') || '—'}
                    </span>
                  </div>
                  <span className={`text-sm font-semibold ${getProfitMarginStyle(profitMargin)}`}>
                    {profitMargin}% Profit Margin
                  </span>
                </div>
              </div>
            );
          })}

          {variants.length === 0 && !isLoading && (
            <div className="text-center py-8">
              <p className="text-sm text-gray-500">
                No variants found. Add variant groups first to generate combinations.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const formatPricingRules = (rules: PricingRule[]): string => {
    if (!rules.length) {
      return '';
    }

    const sorted = [...rules].sort((a, b) => Number(a.price) - Number(b.price));

    return sorted
      .map((rule, idx) => {
        const fromPrice = `₹${convertNumberOrStringToPriceFormat(Number(rule.price))}`;
        const nextRule = sorted[idx + 1];

        if (nextRule) {
          const toPrice = `₹${convertNumberOrStringToPriceFormat(Number(nextRule.price))}`;

          return `${fromPrice} <Price< ${toPrice} = ${rule.gst}%`;
        }

        return `${fromPrice} <Price< ∞ = ${rule.gst}%`;
      })
      .join(' ; ');
  };

  const renderTaxSection = () => {
    const taxes = item?.taxes || [];

    // if (taxes.length === 0 && !isLoading) {
    //   return null;
    // }

    return (
      <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold">%</span>
            <p className="font-inter font-bold text-[1.1rem]">Tax</p>
          </div>
          <button
            type="button"
            onClick={() => modifyTaxModalRef.current?.show()}
            className="px-4 py-2 border border-blue-500 text-blue-600 rounded-2xl text-sm font-medium flex items-center gap-2 hover:bg-blue-50 transition-colors"
          >
            <FaPencilAlt size={12} />
            <span>Modify</span>
          </button>
        </div>
        <p className="font-inter text-sm text-gray-500 mb-5">
          Set per-piece pricing, minimum/maximum rates, and add stitching, fabric, and custom
          charges. Configure GST or tax values for this piece.
        </p>

        <div className="space-y-3">
          {taxes.map((tax) => {
            const isActive = tax.is_default === true;

            return (
              <div key={tax.id} className="border border-gray-200 rounded-xl p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <span className="inline-block px-3 py-1 bg-gray-100 rounded-md text-sm font-semibold text-gray-800 mb-3">
                      {tax.tax_type === 'FLAT' ? 'Flat' : 'Advanced'}
                    </span>
                    <div className="space-y-1 pl-2">
                      <p className="text-sm text-gray-800">
                        <span className="font-semibold">Tax Name : </span>
                        {tax.tax_name}
                      </p>
                      <p className="text-sm text-gray-800">
                        <span className="font-semibold">Tax Percentage : </span>
                        {tax.tax_type === 'FLAT'
                          ? `${tax.flat_gst}%`
                          : formatPricingRules(tax.pricing_rules)}
                      </p>
                    </div>
                  </div>
                  <div className="flex-shrink-0 ml-4 mt-1">
                    <div
                      className={`w-5 h-5 border-2 rounded flex items-center justify-center ${
                        isActive ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                      }`}
                    >
                      {isActive && (
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                          <path
                            d="M2 6l3 3 5-5"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {taxes.length === 0 && !isLoading && (
            <div className="text-center py-6">
              <p className="text-sm text-gray-500">
                No taxes configured. Click Modify to add taxes.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderProductDescription = () => {
    const description = item?.product_description || '';

    return (
      <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-lg">📋</span>
            <p className="font-inter font-bold text-[1.1rem]">Product Description</p>
          </div>
          <button
            type="button"
            onClick={() => productDescModalRef.current?.show()}
            className="px-4 py-2 border border-blue-500 text-blue-600 rounded-2xl text-sm font-medium flex items-center gap-2 hover:bg-blue-50 transition-colors"
          >
            <FaPencilAlt size={12} />
            <span>Modify</span>
          </button>
        </div>
        <p className="font-inter text-sm text-gray-500 mb-5">
          Add details about this product — features, usage, or any important notes.
        </p>

        {description ? (
          <div
            className="font-inter text-sm text-gray-700 leading-relaxed prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: description }}
          />
        ) : (
          <div className="text-center py-6">
            <p className="text-sm text-gray-500">
              No description added. Click Modify to add a product description.
            </p>
          </div>
        )}
      </div>
    );
  };

  const savePieces = useCallback(
    async (updatedPieces: ProductPiece[]) => {
      setIsSavingPieces(true);

      try {
        const payload = updatedPieces.map((p) => ({
          name: p.name,
          ...(p.image_ref_id ? { image_ref_id: p.image_ref_id } : {}),
          ...(p.image_url ? { image_url: p.image_url } : {}),
        }));

        const response = await api.putRequest(`readymade/${id}`, { pieces: payload });

        if (response.status) {
          toasts('success', 'Pieces updated successfully', 'pieces-update-success');
          fetchProductDetails();
        } else {
          toasts('error', response.message || 'Failed to update pieces', 'pieces-update-error');
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'pieces-update-error');
        }
      } finally {
        setIsSavingPieces(false);
      }
    },
    [id, fetchProductDetails]
  );

  const handleAddPiece = useCallback(() => {
    const trimmed = newPieceName.trim();

    if (!trimmed) {
      toasts('error', 'Please enter a piece name', 'piece-name-required');

      return;
    }

    setIconSelectTarget('new');
    pieceIconSelectorRef.current?.show();
  }, [newPieceName]);

  const handlePieceIconSelect = useCallback(
    (icon: IconType) => {
      const imageRefId = icon.reference_id || icon.id || icon.icon_id || '';
      const imageUrl = icon.image_url || icon.icon_url || icon.url || icon.short_lived_url || '';

      if (iconSelectTarget === 'new') {
        const trimmed = newPieceName.trim();

        if (!trimmed) {
          return;
        }

        const currentPieces = item?.pieces || [];
        const updatedPieces = [
          ...currentPieces,
          { name: trimmed, image_ref_id: imageRefId, image_url: imageUrl },
        ];

        setNewPieceName('');
        setIconSelectTarget(null);
        savePieces(updatedPieces);
      } else if (typeof iconSelectTarget === 'number') {
        const currentPieces = item?.pieces || [];
        const updatedPieces = currentPieces.map((p, i) =>
          i === iconSelectTarget ? { ...p, image_ref_id: imageRefId, image_url: imageUrl } : p
        );

        setIconSelectTarget(null);
        savePieces(updatedPieces);
      }
    },
    [iconSelectTarget, newPieceName, item?.pieces, savePieces]
  );

  const handlePieceImageClick = useCallback((idx: number) => {
    setIconSelectTarget(idx);
    pieceIconSelectorRef.current?.show();
  }, []);

  const handleDeletePiece = useCallback(
    (idx: number) => {
      const currentPieces = item?.pieces || [];
      const updatedPieces = currentPieces.filter((_, i) => i !== idx);

      savePieces(updatedPieces);
    },
    [item?.pieces, savePieces]
  );

  const handleEditPieceSave = useCallback(
    (idx: number) => {
      const trimmed = editingPieceName.trim();

      if (!trimmed) {
        toasts('error', 'Piece name cannot be empty', 'piece-name-required');

        return;
      }

      const currentPieces = item?.pieces || [];
      const updatedPieces = currentPieces.map((p, i) => (i === idx ? { ...p, name: trimmed } : p));

      setEditingPieceIdx(null);
      setEditingPieceName('');
      savePieces(updatedPieces);
    },
    [editingPieceName, item?.pieces, savePieces]
  );

  const renderPiecesSection = () => {
    const pieces = item?.pieces || [];

    return (
      <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-lg">◈</span>
          <p className="font-inter font-bold text-[1.1rem]">Pieces (display only)</p>
        </div>
        <p className="font-inter text-sm text-gray-500 mb-5">
          Helps show different components in product details. Inventory is tracked at variant level,
          not per piece.
        </p>

        <div className="space-y-3">
          {pieces.map((pc, idx) => (
            <div
              key={idx}
              className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3"
            >
              <button
                type="button"
                onClick={() => handlePieceImageClick(idx)}
                disabled={isSavingPieces}
                className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center overflow-hidden flex-shrink-0 cursor-pointer hover:ring-2 hover:ring-blue-400 transition-all disabled:opacity-50"
              >
                {pc.image_url ? (
                  <img
                    src={pc.image_url}
                    alt={pc.name}
                    className="w-full h-full object-cover rounded-lg"
                  />
                ) : (
                  <img
                    src={placeholderPieceIcon}
                    alt="Select icon"
                    className="w-6 h-6 object-contain opacity-80"
                  />
                )}
              </button>

              <div className="flex-1 min-w-0">
                {editingPieceIdx === idx ? (
                  <input
                    type="text"
                    value={editingPieceName}
                    onChange={(e) => setEditingPieceName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleEditPieceSave(idx);
                      }

                      if (e.key === 'Escape') {
                        setEditingPieceIdx(null);
                        setEditingPieceName('');
                      }
                    }}
                    onBlur={() => handleEditPieceSave(idx)}
                    autoFocus
                    className="w-full h-8 px-2 border border-blue-400 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <p className="font-inter font-semibold text-sm text-gray-800 truncate">
                    {pc.name}
                  </p>
                )}
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    setEditingPieceIdx(idx);
                    setEditingPieceName(pc.name);
                  }}
                  disabled={isSavingPieces}
                  className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors disabled:opacity-70"
                >
                  <FaPencilAlt size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => handleDeletePiece(idx)}
                  disabled={isSavingPieces}
                  className="p-1.5 text-red-300 hover:text-red-500 transition-colors disabled:opacity-70"
                >
                  <MdDelete size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-3 mt-4">
          <input
            type="text"
            value={newPieceName}
            onChange={(e) => setNewPieceName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleAddPiece();
              }
            }}
            placeholder="Add another piece"
            disabled={isSavingPieces}
            className="flex-1 h-10 px-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          />
          <button
            type="button"
            onClick={handleAddPiece}
            disabled={isSavingPieces}
            className="px-5 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span>+</span>
            <span>{isSavingPieces ? 'Saving...' : 'Add'}</span>
          </button>
        </div>
      </div>
    );
  };

  const renderWorkFlowSection = () => {
    const steps = productionProcess?.steps || [];
    const MAX_VISIBLE_WORKERS = 4;

    return (
      <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 ">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-lg">🔄</span>
            <p className="font-inter font-bold text-[1.1rem]">Work Flow</p>
          </div>
          <button
            type="button"
            onClick={() => workFlowModalRef.current?.show()}
            className="px-4 py-2 border border-blue-500 text-blue-600 rounded-2xl text-sm font-medium flex items-center gap-2 hover:bg-blue-50 transition-colors"
          >
            <FaPencilAlt size={12} />
            <span>Modify</span>
          </button>
        </div>
        <p className="font-inter text-sm text-gray-500 mb-5">
          For each piece, pick measurements from Darzee&apos;s library (with reference images) or
          add custom fields with your own images.
        </p>

        {steps.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left font-inter font-semibold text-gray-500 pb-3 pr-4 whitespace-nowrap" />
                  <th className="text-left font-inter font-semibold text-gray-500 pb-3 px-4 whitespace-nowrap">
                    Process Days
                  </th>
                  <th className="text-left font-inter font-semibold text-gray-500 pb-3 px-4 whitespace-nowrap">
                    Worker Cost
                  </th>
                  <th className="text-left font-inter font-semibold text-gray-500 pb-3 px-4 whitespace-nowrap">
                    Material Cost
                  </th>
                  <th className="text-left font-inter font-semibold text-gray-500 pb-3 pl-4 whitespace-nowrap">
                    Workers
                  </th>
                </tr>
              </thead>
              <tbody>
                {steps.map((step, idx) => {
                  const workerNames = step.workers.map((w) => w.user_name).filter(Boolean);
                  const visibleWorkers = workerNames.slice(0, MAX_VISIBLE_WORKERS);
                  const remainingCount = workerNames.length - MAX_VISIBLE_WORKERS;

                  return (
                    <tr key={idx} className="border-b border-gray-100 last:border-b-0">
                      <td className="py-3 pr-4 font-inter font-medium text-gray-800 whitespace-nowrap">
                        {step.process_name}
                      </td>
                      <td className="py-3 px-4 font-inter text-gray-600">{step.days_to_be_take}</td>
                      <td className="py-3 px-4 font-inter text-gray-600">
                        {step.worker_cost_to_be_take}
                      </td>
                      <td className="py-3 px-4 font-inter text-gray-600">
                        {step.material_cost_to_be_take}
                      </td>
                      <td className="py-3 pl-4 font-inter text-gray-600">
                        {workerNames.length > 0 ? (
                          <span>
                            {visibleWorkers.join(', ')}
                            {remainingCount > 0 && (
                              <>
                                ,{' '}
                                <span className="text-blue-500 font-medium">
                                  {remainingCount} more
                                </span>
                              </>
                            )}
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-6">
            <p className="text-sm text-gray-500">
              No workflow steps configured. Click Modify to add steps.
            </p>
          </div>
        )}
      </div>
    );
  };

  const renderPreview = () => {
    if (!item) {
      return null;
    }

    const colorGroup = variantGroups.find((g) => isColorGroup(g));
    const sizeGroup = variantGroups.find((g) => g.name.toLowerCase() === 'size');
    const otherGroupsCount = variantGroups.filter(
      (g) => !isColorGroup(g) && g.name.toLowerCase() !== 'size'
    ).length;

    const minVal = item.min_value ?? item.base_selling_price ?? 0;
    const maxVal = item.max_value ?? item.base_selling_price ?? 0;
    const priceStr =
      minVal === maxVal
        ? `₹${convertNumberOrStringToPriceFormat(minVal)}`
        : `₹${convertNumberOrStringToPriceFormat(minVal)} - ₹${convertNumberOrStringToPriceFormat(
            maxVal
          )}`;

    const piecesCount = item.pieces_count ?? item.pieces?.length ?? 0;

    return (
      <div className="bg-[#0D0D0D] rounded-2xl p-5 shadow-lg sticky top-6">
        <div className="flex justify-end mb-3">
          <span className="flex items-center gap-1.5 text-white text-sm font-medium">
            <span className="w-2 h-2 rounded-full bg-green-400 inline-block" />
            Preview
          </span>
        </div>

        <div className="flex gap-5 mb-4">
          <div className="w-[180px] flex-shrink-0">
            <div className="w-full aspect-square rounded-xl bg-[#1A1A1A] border border-gray-700 flex items-center justify-center overflow-hidden relative">
              {item.cover_image_url ? (
                <img
                  src={item.cover_image_url}
                  alt={item.name}
                  className="w-full h-full object-cover rounded-xl"
                />
              ) : (
                <span className="text-5xl">👕</span>
              )}
              {piecesCount > 0 && (
                <span className="absolute top-2 right-2 bg-gray-800 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center border border-gray-600">
                  {piecesCount}
                </span>
              )}
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <p className="font-inter font-bold text-[1rem] text-white mb-3 line-clamp-2 leading-snug">
              {item.display_name || item.name}
            </p>

            {colorGroup && colorGroup.values.length > 0 && (
              <div className="mb-3">
                <p className="text-xs text-gray-400 mb-1.5">
                  Color: <span className="text-white font-medium">{previewColor}</span>
                </p>
                <div className="flex gap-2">
                  {colorGroup.values.map((val) => (
                    <button
                      key={val}
                      type="button"
                      onClick={() => setPreviewColor(val)}
                      className={`w-5 h-5 rounded-full border-2 transition-all ${
                        previewColor === val
                          ? 'border-white scale-110'
                          : 'border-gray-600 hover:border-gray-400'
                      }`}
                      style={{ backgroundColor: getColorHex(val) }}
                    />
                  ))}
                </div>
              </div>
            )}

            {sizeGroup && sizeGroup.values.length > 0 && (
              <div className="mb-3">
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-xs text-gray-400">
                    Size: <span className="text-white font-medium">{previewSize}</span>
                  </p>
                  <button
                    type="button"
                    className="text-xs text-blue-400 hover:text-blue-300 underline"
                  >
                    Size Guide
                  </button>
                </div>
                <div className="flex gap-1.5 flex-wrap">
                  {sizeGroup.values.map((val) => (
                    <button
                      key={val}
                      type="button"
                      onClick={() => setPreviewSize(val)}
                      className={`px-2.5 py-1 border rounded text-xs font-medium transition-all ${
                        previewSize === val
                          ? 'bg-white text-black border-white'
                          : 'bg-transparent text-gray-300 border-gray-500 hover:border-gray-300'
                      }`}
                    >
                      {val}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {otherGroupsCount > 0 && (
              <p className="text-xs text-blue-400 mb-3">+{otherGroupsCount} more options</p>
            )}

            {item.product_description && (
              <div className="mb-3">
                <p className="text-xs font-bold text-gray-200 mb-1">Description</p>
                <p className="text-[11px] text-gray-400 leading-relaxed line-clamp-5">
                  {item.product_description}
                </p>
              </div>
            )}
          </div>
        </div>

        {item.pieces && item.pieces.length > 0 && (
          <div className="flex flex-wrap gap-4 mb-4">
            {item.pieces.map((pc, idx) => (
              <div key={idx} className="flex flex-col items-center gap-1">
                <div className="w-11 h-11 rounded-full bg-[#1A1A1A] border border-gray-700 flex items-center justify-center overflow-hidden">
                  {pc.image_url ? (
                    <img
                      src={pc.image_url}
                      alt={pc.name}
                      className="w-full h-full object-cover rounded-full"
                    />
                  ) : (
                    <span className="text-sm">👕</span>
                  )}
                </div>
                <span className="text-[10px] text-gray-400 text-center max-w-[65px] leading-tight">
                  {pc.name}
                </span>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3 mb-3">
          {piecesCount > 1 && (
            <span className="flex items-center gap-1.5 text-xs text-gray-300">
              <img src={piece} alt="Piece" className="w-3.5 h-3.5 object-contain" />
              Multi Piece
            </span>
          )}
          {item.category && (
            <span className="flex items-center gap-1.5 text-xs text-gray-300">
              <img src={wear} alt="Category" className="w-3.5 h-3.5 object-contain" />
              {item.category}
            </span>
          )}
        </div>

        <div className="pt-3 border-t border-gray-700">
          <p className="font-inter font-bold text-[15px] text-white">
            Item Total: <span>{priceStr}</span>
          </p>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="w-full h-full bg-[#F8F8FD] flex items-center justify-center">
        <Text color="black">Loading product data...</Text>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="w-full h-full bg-[#F8F8FD] flex items-center justify-center">
        <Text color="black">Product not found</Text>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-[#F8F8FD] py-4 overflow-y-auto">
      {renderHeader()}

      <div className="flex gap-6 px-6">
        <div className="flex-1 max-w-[520px] space-y-6">
          {renderVariantGroups()}
          {renderVariantMatrix()}
          {renderTaxSection()}
          {renderProductDescription()}
          {renderPiecesSection()}
          {renderWorkFlowSection()}
        </div>

        <div className="flex-1 max-w-[640px]">{renderPreview()}</div>
      </div>

      <VariantGroupsModal
        ref={variantGroupsModalRef}
        productId={Number(id)}
        variantGroups={variantGroups}
        suggestions={variantGroupSuggestions}
        onSaved={fetchProductDetails}
      />

      <ModifyVariantModal
        ref={modifyVariantModalRef}
        variant={selectedVariant}
        itemName={item?.display_name || item?.name || ''}
        onSaved={fetchProductDetails}
      />

      <ModifyTaxModal
        ref={modifyTaxModalRef}
        productId={Number(id)}
        taxes={item?.taxes || []}
        onSaved={fetchProductDetails}
      />

      <ProductDescriptionModal
        ref={productDescModalRef}
        productId={Number(id)}
        description={item?.product_description || ''}
        onSaved={fetchProductDetails}
      />

      <IconSelectorModal ref={pieceIconSelectorRef} onIconSelect={handlePieceIconSelect} />

      <WorkFlowModal
        ref={workFlowModalRef}
        productId={Number(id)}
        productName={item?.display_name || item?.name || ''}
        initialSteps={productionProcess?.steps || []}
        onSaved={fetchProductDetails}
      />

      <BulkApplyModal
        ref={bulkApplyModalRef}
        productId={Number(id)}
        variantCount={variants.length}
        itemName={item?.display_name || item?.name || ''}
        unitLabel={variants[0]?.unit?.toLowerCase() || 'pieces'}
        onSaved={fetchProductDetails}
      />
    </div>
  );
};

export default EditProduct;
